import { GridCellParams, GridFilterModel } from "@mui/x-data-grid-pro";
import { MutableRefObject, useCallback, useEffect, useState } from "react";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";

export type SavedFilterRef = MutableRefObject<GridFilterModel | null>;

export const useFocusGridRow = (
  filterModel: GridFilterModel,
  fieldName: string,
  savedFilterRef?: SavedFilterRef,
) => {
  const { apiRef, pageType } = useDataGridSettings();
  const statusView = pageType === "statusMonitor";
  // Fallback state for pages that don't provide a shared ref
  const [origFilterModel, setOrigFilterModel] = useState<GridFilterModel>();

  // Focus specified row on Name click
  const focusRow = useCallback(
    (params: GridCellParams) => {
      if (statusView) return;
      if (params.field === fieldName) {
        if (savedFilterRef) {
          if (!savedFilterRef.current) savedFilterRef.current = filterModel;
        } else {
          setOrigFilterModel(filterModel);
        }
        apiRef.current.setFilterModel({ items: [], quickFilterValues: [params.row?.device?.name] });
        apiRef.current.setExpandedDetailPanels([params.id]);
      }
    },
    [apiRef, fieldName, filterModel, statusView, savedFilterRef],
  );

  // Exit from the details view on escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (statusView) return;
      if (event.key === "Escape") {
        event.preventDefault();
        apiRef.current.setExpandedDetailPanels([]);
        if (savedFilterRef?.current) {
          apiRef.current.setFilterModel(savedFilterRef.current);
          savedFilterRef.current = null;
        } else if (origFilterModel) {
          apiRef.current.setFilterModel(origFilterModel);
          setOrigFilterModel(undefined);
        } else {
          apiRef.current.setQuickFilterValues([]);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [apiRef, origFilterModel, statusView, savedFilterRef]);

  return focusRow;
};
